/**
 * The companies on one list. Clicking a row (or Enter/Space on it) opens the
 * company in the side panel; the column headers sort server-side.
 *
 * Every value is read from the shared company record, so a blank cell means
 * PitchBook did not give us that field (hidden columns in the recruiter's
 * view, or a company without the data) -- not that the list lost it.
 */
import type { CompanyListMember } from "@nu-assist/contracts";
import { Avatar } from "@nu-assist/ui";
import { ArrowDown, ArrowUp, ArrowUpDown, Linkedin } from "lucide-react";
import type { ReactNode } from "react";

import type { CompanyMemberSortKey } from "../../clients/companyLists.client";
import {
  formatCount,
  formatHq,
  formatUsdMillions,
  linkedinHref,
  pitchbookHref,
  websiteLabel,
} from "./format";
import type { MemberSort } from "./queries";

interface Column {
  label: string;
  sortKey?: CompanyMemberSortKey;
  numeric?: boolean;
}

const COLUMNS: readonly Column[] = [
  { label: "Company", sortKey: "name" },
  { label: "Links" },
  { label: "HQ", sortKey: "hq_country" },
  { label: "Sector", sortKey: "sector" },
  { label: "Employees", sortKey: "employees", numeric: true },
  { label: "Revenue", sortKey: "revenue_usd_m", numeric: true },
  { label: "Founded", sortKey: "founded_year", numeric: true },
  { label: "We know", sortKey: "people_known", numeric: true },
];

interface CompanyMembersTableProps {
  items: readonly CompanyListMember[];
  sort: MemberSort;
  onSort: (key: CompanyMemberSortKey) => void;
  openCompanyId: string | null;
  onOpen: (companyId: string) => void;
  /** Presigned logo URLs by `logo_s3_key`; a company without one shows initials. */
  logoUrls?: ReadonlyMap<string, string>;
  /** Shown under the company's name: the note that PitchBook gave neither a
   *  website nor a LinkedIn page. */
  belowName?: (row: CompanyListMember) => ReactNode;
  /** A filter is on: an empty page then means "nothing matches", not "this
   *  list is empty" (People Lists' "No contacts match the current filters."). */
  filtered?: boolean;
}

const dash = <span className="text-text-muted">—</span>;

export function CompanyMembersTable({
  items,
  sort,
  onSort,
  openCompanyId,
  onOpen,
  logoUrls,
  belowName,
  filtered = false,
}: CompanyMembersTableProps) {
  if (items.length === 0 && filtered) {
    return (
      <div
        data-testid="company-members-no-match"
        className="rounded-md border border-border bg-surface px-4 py-12 text-center text-body text-text-secondary"
      >
        No companies match the current filters.
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        data-testid="company-members-empty"
        className="rounded-md border border-border bg-surface px-4 py-12 text-center text-body text-text-secondary"
      >
        This list has no companies yet.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-table-surface">
      <table className="w-full border-collapse" data-testid="company-members-table">
        <thead>
          <tr className="border-b border-border text-left">
            {COLUMNS.map((col) => {
              const active = col.sortKey !== undefined && sort.key === col.sortKey;
              return (
                <th
                  key={col.label}
                  scope="col"
                  aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
                  className="text-label font-semibold text-text-secondary"
                >
                  {col.sortKey ? (
                    <button
                      type="button"
                      onClick={() => col.sortKey && onSort(col.sortKey)}
                      className={`inline-flex w-full select-none items-center gap-1 px-3 py-2 hover:text-text ${
                        col.numeric ? "justify-end" : "text-left"
                      }${active ? " text-accent" : ""}`}
                    >
                      {col.label}
                      {active ? (
                        sort.dir === "asc" ? (
                          <ArrowUp aria-hidden="true" className="h-3 w-3 text-accent" />
                        ) : (
                          <ArrowDown aria-hidden="true" className="h-3 w-3 text-accent" />
                        )
                      ) : (
                        <ArrowUpDown aria-hidden="true" className="h-3 w-3 text-text-muted" />
                      )}
                    </button>
                  ) : (
                    <span className="block px-3 py-2">{col.label}</span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const isOpen = row.company_id === openCompanyId;
            const open = () => onOpen(row.company_id);
            // PitchBook's scraped "City, ST" lands in hq_state (for non-US rows
            // the country lands there too); a CSV import fills hq_country.
            const hq = formatHq([row.hq_city, row.hq_state || row.hq_country]);
            const revenue = formatUsdMillions(row.revenue_usd_m);
            const employees = formatCount(row.employees);
            // The row's aria-label replaces its content for a screen reader;
            // what sits under the name (the no-website note) is its description.
            const below = belowName?.(row);
            const belowId = below ? `company-row-below-${row.company_id}` : undefined;
            return (
              <tr
                key={row.company_id}
                tabIndex={0}
                aria-label={`Open ${row.name ?? "company"} in the side panel`}
                aria-describedby={belowId}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                data-testid="company-member-row"
                data-company-row={row.company_id}
                onClick={open}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    open();
                  }
                }}
                className={`cursor-pointer border-b border-border last:border-b-0 hover:bg-row-hover focus:bg-row-hover focus:outline-none${
                  isOpen ? " bg-row-hover" : ""
                }`}
              >
                <td className="px-3 py-2">
                  <span className="flex items-center gap-3">
                    <Avatar
                      name={row.name}
                      src={row.logo_s3_key ? logoUrls?.get(row.logo_s3_key) : null}
                      shape="square"
                      size="md"
                    />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-body font-medium text-text">
                        {row.name ?? "Unnamed company"}
                      </span>
                      {row.url && (
                        <span className="truncate text-caption text-text-muted">
                          {websiteLabel(row.url)}
                        </span>
                      )}
                      {below && <span id={belowId}>{below}</span>}
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2" data-testid="company-member-links">
                  <CompanyLinks row={row} />
                </td>
                <td className="px-3 py-2 text-body text-text-secondary">{hq ?? dash}</td>
                <td className="px-3 py-2 text-body text-text-secondary">{row.sector ?? dash}</td>
                <td className="px-3 py-2 text-right text-body tabular-nums text-text">
                  {employees ?? dash}
                </td>
                <td className="px-3 py-2 text-right text-body tabular-nums text-text">
                  {revenue ?? dash}
                </td>
                <td className="px-3 py-2 text-right text-body tabular-nums text-text-secondary">
                  {row.founded_year ?? dash}
                </td>
                <td
                  className="px-3 py-2 text-right text-body font-semibold tabular-nums text-accent-ink"
                  title="People from our lists who have worked here"
                >
                  {row.people_known ? row.people_known : dash}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The company's LinkedIn page and PitchBook profile as icons, like the people
 * list's LinkedIn column. A click stays on the link: the row itself opens the
 * side panel, and a new tab is what the recruiter asked for here.
 */
function CompanyLinks({ row }: { row: CompanyListMember }) {
  const linkedin = linkedinHref(row.linkedin_url);
  const pitchbook = pitchbookHref(row.pitchbook_id);
  if (!linkedin && !pitchbook) return dash;
  const name = row.name ?? "this company";
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();
  return (
    <span className="inline-flex items-center gap-1">
      {linkedin && (
        <a
          href={linkedin}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open ${name} on LinkedIn`}
          title="Open on LinkedIn"
          onClick={stop}
          onKeyDown={stop}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-accent-ink hover:bg-accent-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <Linkedin className="h-4 w-4" aria-hidden="true" />
        </a>
      )}
      {pitchbook && (
        <a
          href={pitchbook}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open ${name} on PitchBook`}
          title="Open on PitchBook"
          onClick={stop}
          onKeyDown={stop}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-accent-ink hover:bg-accent-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {/* No PitchBook glyph in lucide-react: the PitchBook logo from public/. */}
          <img src="/pitchbook.png" alt="" aria-hidden="true" className="h-4 w-4 rounded-xs" />
        </a>
      )}
    </span>
  );
}
